import { MotionConfig } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ApiError, api } from './api/client';
import { Layout } from './components/Layout';
import { markOrgChooserUnseen } from './components/OrgChooserModal';
import { ToastProvider } from './components/Toast';
import { LanguageProvider } from './lib/i18n/LanguageContext';
import { SessionProvider } from './lib/SessionContext';
import { ThemeProvider } from './lib/theme';
import { AcceptInvite } from './pages/AcceptInvite';
import { Dashboard } from './pages/Dashboard';
import { EditProfile } from './pages/EditProfile';
import { ForgotPassword } from './pages/ForgotPassword';
import { Guide } from './pages/Guide';
import { Keys } from './pages/Keys';
import { Models } from './pages/Models';
import { Notifications } from './pages/Notifications';
import { Playground } from './pages/Playground';
import { Reports } from './pages/Reports';
import { RequiredTwoFactorSetup } from './pages/RequiredTwoFactorSetup';
import { ResetPassword } from './pages/ResetPassword';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { Team } from './pages/Team';
import { TopUp } from './pages/TopUp';
import { Usage } from './pages/Usage';
import { VerifyEmail } from './pages/VerifyEmail';
import { AuthCredentialsLayout } from './components/AuthCredentialsLayout';

/**
 * 'two-factor-setup-required' is a self-service b2b org owner/admin who has
 * a session (verify-email issued one specifically for this) but mandatory
 * TOTP and zero enrolled factors — see LLMR-b2b-self-signup's first-login
 * deadlock fix. Checked on every boot, not just right after verify-email,
 * so a reload or a direct URL into any other page lands back on the forced
 * setup screen too instead of the rest of the app.
 */
type State = 'checking' | 'anonymous' | 'signed-in' | 'two-factor-setup-required';

const BOOT_SPLASH_ID = 'boot-splash';

/** Paths that must paint during `checking` — boot splash would cover them. */
function isPublicAuthPath(pathname: string): boolean {
  return (
    pathname === '/verify-email' ||
    pathname === '/reset-password' ||
    pathname === '/accept-invite'
  );
}

function dismissBootSplash() {
  document.getElementById(BOOT_SPLASH_ID)?.remove();
}

/** BrowserRouter keeps scroll position across route changes by default —
 * without this, leaving a long page (Usage, Models) mid-scroll lands the
 * next page at the same Y offset instead of its top. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Covers the auth gate with a same-color shell so Layout can mount under it.
 * Never reveals the IC spinner itself — index.html only does that after 1200ms
 * if this node is still present (slow boot). Fast /me → remove before then.
 */
function BootSplashGate({ authState }: { authState: State }) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (authState !== 'checking' || isPublicAuthPath(pathname)) {
      dismissBootSplash();
    }
  }, [authState, pathname]);

  return null;
}

/** Placeholder route while `/me` runs — the visible loader is `#boot-splash`. */
function AuthChecking() {
  return <div className="center" aria-busy="true" />;
}

/** SessionProvider only needs to wrap the signed-in route subtree — pulled
 * out as its own component so it can be used as a single layout Route's
 * `element`, rather than wrapping a whole extra <Routes> tree (see the note
 * on the single shared <Routes> below for why that would reintroduce the
 * remount bug this file was rewritten to fix). */
function SignedInShell({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <SessionProvider>
      <Layout onSignOut={onSignOut} />
    </SessionProvider>
  );
}

/**
 * Anonymous catch-all. A link straight into a signed-in-only path (e.g. the
 * website's "Get API key" pointing at /keys) lands here before the visitor
 * has a session — carries the path forward as `next` so SignIn/SignUp can
 * send them on to it afterward instead of always landing on /dashboard.
 */
function RedirectToLogin() {
  const location = useLocation();
  const next = location.pathname === '/' ? '' : `?next=${encodeURIComponent(location.pathname)}`;
  return <Navigate to={`/login${next}`} replace />;
}

export function App() {
  const [state, setState] = useState<State>('checking');

  // A stable identity, not an inline arrow at the JSX call site: VerifyEmail's
  // own mount effect depends on this callback, and App re-renders on every
  // state change (including the unrelated checking → anonymous/signed-in
  // transition racing right alongside verify-email's own request). A new
  // function reference each render would re-fire that effect and send a
  // second real POST /auth/verify-email for the same one-time token — the
  // exact remount bug VerifyEmail.tsx's own CRITICAL comment already
  // documents, just reintroduced through a prop instead of the routing tree.
  const handleTwoFactorSetupRequired = useCallback(() => setState('two-factor-setup-required'), []);

  const handleSignOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      markOrgChooserUnseen();
      setState('anonymous');
    }
  }, []);

  // Session state comes from the server. The session cookie is HttpOnly, so
  // there is nothing for JavaScript to inspect — asking /me is the only
  // honest way to know whether we are signed in.
  //
  // A second check right after /me succeeds: GET /me/2fa/methods' own
  // `enforced && enrolled.length === 0` is the same signal EditProfile
  // already reads to show its passive note, but this is the one place that
  // turns it into an actual gate — every boot, every reload, every direct
  // URL, not just the moment right after verify-email issued the session
  // (see VerifyEmail's onTwoFactorSetupRequired for that first hop). Normal
  // /auth/login can never produce this combination itself — it already
  // refuses a session outright when 2FA is mandatory and nothing is
  // enrolled — so this only ever fires for the b2b deadlock fix's session.
  const check = useCallback(async () => {
    try {
      await api.me();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setState('anonymous');
      else setState('anonymous');
      return;
    }
    try {
      const methods = await api.twoFactorMethods();
      if (methods.enforced && methods.enrolled.length === 0) {
        setState('two-factor-setup-required');
        return;
      }
    } catch {
      // Fails open to the normal signed-in state: this call is a UX
      // convenience gate, not the security boundary — handleLogin already
      // refuses a session outright for an account in this shape, so a
      // transient failure here cannot itself let anyone bypass mandatory 2FA.
    }
    setState('signed-in');
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  return (
    // Language wraps theme (and everything else) — SignIn/SignUp render both
    // switches while anonymous, so both providers need to be above the
    // "anonymous" branch below, not just the signed-in shell.
    <LanguageProvider>
      <ThemeProvider>
        <ToastProvider>
        {/* One motion library, one place it is configured. `reducedMotion="user"`
            makes every `motion.*` element in the tree honour the OS
            prefers-reduced-motion setting automatically — transforms collapse
            to instant, opacity fades survive as the minimum acknowledgement
            that something changed. */}
        <MotionConfig reducedMotion="user">
          {/* BrowserRouter wraps every state, not just signed-in: /login and
              /signup are now real, bookmarkable URLs.

              CRITICAL: this is ONE <Routes> tree, not one per `state`. An
              earlier version rendered a separate <Routes> per state, each
              with its own <Route path="/verify-email">. That meant the
              checking → anonymous/signed-in transition — which happens on
              every load, as soon as api.me() resolves — unmounted the
              "checking" tree's <VerifyEmail/> and mounted a brand new
              instance from the next tree's. Since VerifyEmail's mount effect
              calls the one-time-use POST /auth/verify-email, that remount
              fired a SECOND real consume-token request with the same token —
              a race that could burn the token on the throwaway first mount
              and leave the surviving instance showing "invalid or expired"
              for a link that had, in fact, just succeeded. Not a StrictMode
              artifact: StrictMode's dev-only double-invoke made it worse (4
              requests instead of 2), but the remount itself happens in
              production too, non-deterministically, depending on how fast
              /me resolves relative to the verify call.

              Keeping /verify-email in the SAME <Routes> tree across every
              state means React Router resolves it to the same component
              instance regardless of what the sibling routes around it are —
              its mount effect fires exactly once per real page load. */}
          <BrowserRouter>
            <ScrollToTop />
            <BootSplashGate authState={state} />
            <Routes>
              <Route
                path="/verify-email"
                element={<VerifyEmail onTwoFactorSetupRequired={handleTwoFactorSetupRequired} />}
              />
              {/* Same shared-tree rule as /verify-email: the emailed reset
                  link must not remount when auth state settles, or a
                  one-time token could be consumed twice. */}
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Also outside the state branches: accepting an invite creates
                  a brand new account regardless of whatever session (or lack
                  of one) this browser currently holds — see AcceptInvite's
                  own note on why it does not need VerifyEmail's remount
                  workaround even though it lives in the same shared tree. */}
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {state === 'checking' && <Route path="*" element={<AuthChecking />} />}

              {state === 'anonymous' && (
                <>
                  <Route element={<AuthCredentialsLayout />}>
                    <Route
                      path="/login"
                      element={<SignIn onSignedIn={() => setState('signed-in')} />}
                    />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                  </Route>
                  <Route path="*" element={<RedirectToLogin />} />
                </>
              )}

              {state === 'signed-in' && (
                <Route element={<SignedInShell onSignOut={handleSignOut} />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/keys" element={<Keys />} />
                  <Route path="/usage" element={<Usage />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/models" element={<Models />} />
                  <Route path="/playground" element={<Playground />} />
                  <Route path="/topup" element={<TopUp />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/guide" element={<Guide />} />
                  <Route path="/profile/edit" element={<EditProfile />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
              )}

              {/* Replaces the ENTIRE signed-in route tree, not one route
                  among Layout's children — a direct URL into any other page
                  must land back here too, same as a reload does via check()
                  above. See LLMR-b2b-self-signup's first-login deadlock fix. */}
              {state === 'two-factor-setup-required' && (
                <Route
                  path="*"
                  element={
                    <RequiredTwoFactorSetup onComplete={() => setState('signed-in')} onSignOut={handleSignOut} />
                  }
                />
              )}
            </Routes>
          </BrowserRouter>
        </MotionConfig>
        </ToastProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}
